import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { ApolloError } from 'apollo-client';

import assignmentInfoQuery from 'bundles/grading/api/AssignmentInfoQuery.graphql';
import type {
  AssignmentInfoQuery,
  AssignmentInfoQueryVariables,
  GradingStatusSummary,
} from 'bundles/grading/api/types';

type AssignmentInfoProcessed = {
  /**
   * Name of the current assignment
   */
  currentItemName: string;
  /**
   * Summary of grading progress statuses for entire assignment
   */
  gradingStatusSummary: GradingStatusSummary;
};

export type UseAssignmentInfoQueryResult = {
  loading: boolean;
  data?: AssignmentInfoProcessed;
  error?: ApolloError;
};

const useAssignmentInfoQuery = (variables: AssignmentInfoQueryVariables, skip: boolean = false) => {
  const { data, loading, error } = useQuery<AssignmentInfoQuery, AssignmentInfoQueryVariables>(assignmentInfoQuery, {
    variables,
    context: { clientName: 'gatewayGql' },
    skip,
    onError: (err) => {
      Sentry.captureException(err);
    },
  });

  const processedData = useMemo(() => {
    if (!data?.GroupGrading?.queryGroup?.items.length) {
      return undefined;
    }
    return {
      currentItemName: data.GroupGrading.queryGroup.items[0].name,
      gradingStatusSummary: data.GroupGrading.queryGroup.items[0].gradingStatusSummary,
    };
  }, [data]);

  return {
    loading,
    data: processedData,
    error,
  };
};

export default useAssignmentInfoQuery;
